// 픽셀 아트를 장면에 넣고 되읽는 순수 도우미. CanvasEditor를 가볍게 두려고 따로 뺐다.
import { convertToExcalidrawElements } from "@excalidraw/excalidraw";
import {
  generateIdFromFile,
  getDataURL,
} from "@excalidraw/excalidraw/data/blob";

import { COLS, ROWS, fromImageData, renderPngBlob } from "./pixelGrid";

import type {
  ExcalidrawElement,
  ExcalidrawImageElement,
  FileId,
} from "@excalidraw/excalidraw/element/types";
import type { DataURL } from "@excalidraw/excalidraw/types";
import type { PixelGrid } from "./pixelGrid";

/** 장면에 놓이는 그림 한 변(px). 내보내는 PNG는 더 크지만 장면에는 이 크기로 놓는다. */
export const PIXEL_ART_SCENE_SIZE = 400;

export type PixelArtFile = { fileId: FileId; dataURL: DataURL };

/** 격자를 PNG로 만들어 파일 id와 dataURL을 얻는다. */
export const buildPixelArtFile = async (
  grid: PixelGrid,
): Promise<PixelArtFile> => {
  const blob = await renderPngBlob(grid);
  const file = new File([blob], "pixel-art.png", { type: "image/png" });
  const [fileId, dataURL] = await Promise.all([
    generateIdFromFile(file),
    getDataURL(file),
  ]);
  return { fileId, dataURL };
};

/** 클릭점이 그림 중심이 되도록 400×400 이미지 요소를 만든다. */
export const createPixelArtElement = (
  fileId: FileId,
  center: { x: number; y: number },
): ExcalidrawImageElement => {
  const [image] = convertToExcalidrawElements([
    {
      type: "image",
      x: center.x - PIXEL_ART_SCENE_SIZE / 2,
      y: center.y - PIXEL_ART_SCENE_SIZE / 2,
      width: PIXEL_ART_SCENE_SIZE,
      height: PIXEL_ART_SCENE_SIZE,
      fileId,
      status: "saved",
      customData: { canvaschool: { pixelArt: { cols: COLS, rows: ROWS } } },
    },
  ]);
  return image as ExcalidrawImageElement;
};

/** 픽셀 아트 이미지 요소인가(수정 버튼을 보일 후보). */
export const isPixelArtImage = (
  element: ExcalidrawElement,
): element is ExcalidrawImageElement =>
  element.type === "image" &&
  !!(element.customData as { canvaschool?: { pixelArt?: unknown } } | undefined)
    ?.canvaschool?.pixelArt;

/** 회전을 반영한 바깥 상자의 오른쪽 변: x와 위·아래 y(장면 좌표). */
export const rotatedRightEdge = (element: ExcalidrawElement) => {
  const cx = element.x + element.width / 2;
  const cy = element.y + element.height / 2;
  const cos = Math.abs(Math.cos(element.angle));
  const sin = Math.abs(Math.sin(element.angle));
  const halfW = (element.width * cos + element.height * sin) / 2;
  const halfH = (element.width * sin + element.height * cos) / 2;
  return { x: cx + halfW, minY: cy - halfH, maxY: cy + halfH };
};

/** dataURL 그림을 원본 크기 그대로, 보간 없이 읽어 격자로 되돌린다. */
export const decodePixelArt = (dataURL: string): Promise<PixelGrid> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const ctx = canvas.getContext("2d");
        if (!ctx || canvas.width === 0 || canvas.height === 0) {
          throw new Error("canvas unavailable");
        }
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(image, 0, 0);
        resolve(
          fromImageData(
            ctx.getImageData(0, 0, canvas.width, canvas.height),
            COLS,
            ROWS,
          ),
        );
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = () => reject(new Error("image decode failed"));
    image.src = dataURL;
  });
